// src/components/layout/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  MessageSquare,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Heart,
  ExternalLink
} from 'lucide-react';
import ShareButton from '../shared/ShareButton';
import { DDD_PRIVACY_URL, DDD_TERMS_URL, DDD_COOKIES_URL, openLegalPage } from '../../config/legalLinks';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Inside the iOS app: a compact, store-safe footer (Pemberton's approved pattern).
  // The tab bar does the navigating, and the web footer's other-apps list and social
  // links send people out of the app, some towards purchases made outside the store
  // (App Store rule 3.1.1).
  if (Capacitor.isNativePlatform()) {
    return (
      <footer className="relative mt-12 border-t border-slate-800">
        <div className="container mx-auto px-4 py-6 text-center space-y-3">
          <nav aria-label="Legal and help" className="flex items-center justify-center gap-5 text-sm">
            <Link to="/privacy" className="text-pearl/70 hover:text-bright-gold transition-colors">Privacy</Link>
            <button type="button" onClick={() => openLegalPage(DDD_TERMS_URL)} className="text-pearl/70 hover:text-bright-gold transition-colors">Terms</button>
            <Link to="/support" className="text-pearl/70 hover:text-bright-gold transition-colors">Support</Link>
          </nav>
          <p className="text-xs text-slate-400">
            © {currentYear} Profound Philosophy by SILVER WOLF Productions
          </p>
        </div>
      </footer>
    );
  }

  const dddApps = [
    {
      name: 'The Art of Story',
      href: 'https://artofstory.divedeepdialogues.com',
      description: 'Master magnetic storytelling'
    },
    {
      name: 'Deeper Dialogues',
      href: 'https://deeperdialogues.divedeepdialogues.com',
      description: 'How to make the people in your life feel heard'
    },
    {
      name: 'The Art of Conversation',
      href: 'https://artofconversation.divedeepdialogues.com',
      description: 'Master meaningful dialogue'
    },
    {
      name: 'Inner Voice Mastery',
      href: 'https://innervoice.divedeepdialogues.com',
      description: 'Transform your self-talk'
    },
    {
      name: 'Dive Deep Workplace',
      href: 'https://divedeepworkplace.com',
      description: 'Better conversations at work'
    }
  ];

  const quickLinks = [
    { name: 'Assessment', href: '/assessment' },
    { name: 'Learning Modules', href: '/modules' },
    { name: 'Toolkit', href: '/toolkit' },
    { name: 'About DDD', href: 'https://divedeepdialogues.com', external: true },
    { name: 'Data & Privacy Settings', href: '/privacy' },
    // Legal policies are DDD-wide (src/config/legalLinks.js).
    { name: 'Privacy Policy', href: DDD_PRIVACY_URL, external: true },
    { name: 'Terms of Use', href: DDD_TERMS_URL, external: true },
    { name: 'Cookie Policy', href: DDD_COOKIES_URL, external: true }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61565456445914' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/dive-deep-dialogues' }
  ];

  return (
    <footer className="relative mt-20 border-t border-slate-800">
      <div className="container mx-auto px-4 py-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4 group">
              <img
                src="/profound-philosophy-phi-128.png"
                alt="Profound Philosophy"
                className="w-12 h-12 rounded-lg group-hover:scale-110 transition-transform"
              />
              <div className="flex flex-col">
                <h3 className="text-white text-sm font-bold group-hover:text-bright-gold transition-colors" style={{ lineHeight: '1.2' }}>Profound Philosophy</h3>
                <p className="text-xs text-muted-gold group-hover:text-gold transition-colors" style={{ lineHeight: '1', marginTop: '2px' }}>by Dive Deep Dialogues</p>
              </div>
            </Link>
            <p className="text-sm text-slate-400 mb-4">
              Learn to think like a philosopher: 2,500 years of humanity's deepest questions, turned into modules and tools you can practise daily.
            </p>

            {/* Social Links */}
            <div className="flex gap-2">
              {socialLinks.map(social => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-purple-600/20 rounded-lg flex items-center justify-center transition-colors"
                    aria-label={`${social.name} (opens in new tab)`}
                  >
                    <Icon className="w-4 h-4 text-slate-400 hover:text-purple-400" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Other DDD Apps */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Explore Our Apps</h4>
            <ul className="space-y-3">
              {dddApps.map(app => (
                <li key={app.name}>
                  <a
                    href={app.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="flex items-start gap-1">
                      <span className="text-sm text-white group-hover:text-bright-gold transition-colors font-medium">
                        {app.name}
                      </span>
                      <span className="sr-only"> (opens in new tab)</span>
                      <ExternalLink className="w-3 h-3 text-slate-400 mt-0.5" />
                    </div>
                    <p className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors" style={{ marginTop: '2px' }}>
                      {app.description}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map(link => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-bright-gold text-sm transition-colors inline-flex items-center gap-1 py-1"
                    >
                      {link.name}
                      <span className="sr-only"> (opens in new tab)</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-slate-400 hover:text-bright-gold text-sm transition-colors inline-block py-1"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Daily Philosophy</h4>
            <p className="text-xs text-slate-400 mb-3">
              A new philosophy tip and a weekly challenge live on your Dashboard.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Today's tip
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-slate-800">
          {/* The central terms have no "not professional advice" clause, and the
              in-app terms that held one are gone, so it lives here. */}
          <p className="text-xs text-slate-400 text-center md:text-left mb-4">
            Profound Philosophy is educational content, not professional advice, counselling or crisis support. If you or someone else is in danger, see{' '}
            <Link to="/support" className="text-amber-300 hover:text-white underline">Help &amp; Support</Link>{' '}
            or call your local emergency number.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-400">
              © {currentYear} Profound Philosophy by SILVER WOLF Productions
            </p>

            <p className="text-xs text-slate-400 flex items-center gap-1">
              Made with <Heart className="w-3 h-3 text-red-400" /> in Australia
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
